import { useEffect, useState } from 'preact/hooks';
import { api } from '../api';
import { useGlobe } from '../globe';
import type { T } from '../i18n';
import { errorText, refreshTransit, set, toast } from '../state';
import type { AppState } from '../state';
import { countryLabel } from '../countries';
import type { Lang, Postcard } from '../types';
import { MES } from '../util';
import { CrossIcon, EnvelopeGlyph } from './Icons';

// The three hand-drawn routes from the design, used in turn.
const ROUTES = [
  'M22 62 C86 62 104 24 148 24 C186 24 196 60 166 60 C138 60 150 20 200 24 C272 30 288 66 348 62 C404 58 420 22 470 24 C512 26 524 52 538 44',
  'M22 30 C70 30 78 66 122 64 C168 62 172 26 216 26 C258 26 262 58 236 60 C210 62 218 28 262 30 C330 34 356 68 414 62 C466 56 480 26 538 32',
  'M22 54 C60 54 70 20 112 20 C160 20 168 62 214 62 C256 62 268 24 306 24 C344 24 352 56 322 58 C296 60 306 22 352 26 C420 32 448 62 500 56 C522 53 530 46 538 40',
];
const DOTS = ['0.1 7', '0.1 5', '0.1 9'];
const INKS = ['#1B2A22', '#2B4A8B', '#7A5A2E'];

/**
 * Your own postcards that are still in the mail. Cards on their way to you are
 * never listed here — the API does not even return them — so a postcard
 * stays a surprise until it lands.
 */
export function Track({ s, t }: { s: AppState; t: T }) {
  return (
    <div class="panel">
      <div class="title">{t('trackTitle')}</div>
      <div class="subtitle">{t('trackSub')}</div>
      <div class="rule">
        <span>{t('outgoing')}</span>
        <span />
      </div>
      {s.outgoing.length === 0 ? (
        <div class="empty">{t('emptyOut')}</div>
      ) : (
        <div class="transit-list">
          {s.outgoing.map((card, i) => (
            <TransitCard key={card.id} card={card} index={i} lang={s.lang} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Progress comes from the card's own timestamps, so it is right after any reload. */
function progressOf(card: Postcard, now: number): number {
  const start = Date.parse(card.createdAt);
  const end = Date.parse(card.deliverAt);
  if (!(end > start)) return 1;
  return Math.min(1, Math.max(0, (now - start) / (end - start)));
}

function countdown(ms: number): string {
  const left = Math.max(0, ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  const days = Math.floor(left / 864e5);
  const hours = Math.floor(left / 36e5) % 24;
  const minutes = Math.floor(left / 6e4) % 60;
  const seconds = Math.floor(left / 1e3) % 60;
  return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

// Routes are drawn in a 560×88 viewBox so they scale down on narrow screens.
// CSS offset-path works in unscaled pixels, so the envelope is placed by
// measuring the path instead.
const SVG_NS = 'http://www.w3.org/2000/svg';
const measured = new Map<string, SVGPathElement>();

function pointOnRoute(d: string, fraction: number): { x: number; y: number; angle: number } {
  let path = measured.get(d);
  if (!path) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;visibility:hidden';
    path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
    document.body.appendChild(svg);
    measured.set(d, path);
  }
  const length = path.getTotalLength();
  const at = fraction * length;
  const here = path.getPointAtLength(at);
  const before = path.getPointAtLength(Math.max(0, at - 1));
  const after = path.getPointAtLength(Math.min(length, at + 1));
  return { x: here.x, y: here.y, angle: (Math.atan2(after.y - before.y, after.x - before.x) * 180) / Math.PI };
}

function TransitCard({ card, index, lang, t }: { card: Postcard; index: number; lang: Lang; t: T }) {
  const now = useNow(1000);
  const progress = progressOf(card, now);
  const route = ROUTES[index % ROUTES.length]!;
  const ink = INKS[index % INKS.length]!;
  const pip = pointOnRoute(route, progress);
  const arrival = new Date(card.deliverAt);

  return (
    <div class="transit">
      <div class="transit-rings" />
      <TransitGlobe />
      <div class="transit-body">
        <div class="transit-head">
          <span class="transit-who">
            {t('you')} → {card.to}
          </span>
          <span class="transit-when">
            <span class="transit-clock">{countdown(arrival.getTime() - now)}</span>
            <span class="transit-arrives">
              {t('arrives')} {arrival.getDate()} {MES[arrival.getMonth()]}
            </span>
          </span>
        </div>

        <div class="route">
          <svg viewBox="0 0 560 88" fill="none" aria-hidden="true">
            <path d={route} stroke="rgba(27,42,34,.34)" stroke-width="2.2" stroke-linecap="round" stroke-dasharray={DOTS[index % DOTS.length]} />
            <path d={route} pathLength={100} stroke={ink} stroke-width="2.6" stroke-linecap="round" stroke-dasharray={`${(progress * 100).toFixed(2)} 100`} />
            <g transform={`translate(${pip.x.toFixed(1)} ${pip.y.toFixed(1)}) rotate(${pip.angle.toFixed(1)}) translate(-15 -24)`}>
              <EnvelopeGlyph ink={ink} />
            </g>
          </svg>
        </div>

        <div class="transit-foot">
          <span>{countryLabel(card.fromCountry, lang)}</span>
          <button class="cancel-btn" onClick={() => set({ confirmCancelId: card.id })}>
            <CrossIcon />
            <span>{t('cancel')}</span>
          </button>
          <span>{countryLabel(card.toCountry, lang)}</span>
        </div>
      </div>
    </div>
  );
}

/** The world turning faintly behind each card, as in the design. */
function TransitGlobe() {
  const globe = useGlobe();
  return (
    <svg class="transit-globe" viewBox="0 0 100 100" aria-hidden="true">
      {globe.land && <path d={globe.land} />}
    </svg>
  );
}

export function ConfirmCancel({ s, t }: { s: AppState; t: T }) {
  const [busy, setBusy] = useState(false);
  const card = s.outgoing.find((c) => c.id === s.confirmCancelId);
  if (!card) return null;

  const dismiss = () => set({ confirmCancelId: null });
  const confirm = async () => {
    setBusy(true);
    try {
      await api.cancel(card.id);
      set({ confirmCancelId: null });
      await refreshTransit();
    } catch (err) {
      set({ confirmCancelId: null });
      toast(errorText(err, t('genericError')), 'error');
    }
  };

  return (
    <div
      class="scrim scrim-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={t('confirmTitle')}
      onClick={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div class="dialog">
        <div class="dialog-title">{t('confirmTitle')}</div>
        <div class="dialog-body">{t('confirmBody', { who: `${t('you')} → ${card.to}` })}</div>
        <div class="dialog-actions">
          <button class="btn-line" onClick={dismiss}>
            {t('confirmNo')}
          </button>
          <button class="btn-red" disabled={busy} onClick={() => void confirm()}>
            {t('confirmYes')}
          </button>
        </div>
      </div>
    </div>
  );
}
